import { useEffect, useLayoutEffect, useState } from "react";
import Button from "../../../student/components/generic/button";
import { CourseSearch } from "../Search/CourseSearch";
import clsx from "clsx";
import { CourseGroupWithCourses } from "../../types";
import { useMediaQuery } from "usehooks-ts";

export const GroupedCoursesHeader: React.FC<{
  groups: CourseGroupWithCourses[];
  disableSearch?: boolean;
  titleOverride?: string;
}> = ({ groups, disableSearch, titleOverride }) => {
  const [isStuck, setIsStuck] = useState(false);
  const [activeGroup, setActiveGroup] = useState<string | null>(
    groups.length > 0 ? groups[0]._id : null
  );
  const isMobileScreen = useMediaQuery("(max-width: 640px)");

  useEffect(() => {
    const handleScroll = () => {
      const isPastTop = window.scrollY > 24;
      if (!isStuck && isPastTop) {
        setIsStuck(true);
      } else if (isStuck && !isPastTop) {
        setIsStuck(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isStuck]);

  useLayoutEffect(() => {
    const elements = document.querySelectorAll("[data-scrollspy]");

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const rect = entry.boundingClientRect;
          const offset = isMobileScreen ? 220 : 180;
          const isInVewPort = rect.top <= offset && rect.bottom >= offset;
          if (isInVewPort) {
            const groupId = (entry.target as HTMLDivElement).dataset.groupId;
            if (groupId) {
              setActiveGroup(groupId);
              break;
            }
          }
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: Array(20)
          .fill(0)
          .map((_, index) => (index * 5) / 100),
      }
    );
    elements.forEach((el) => {
      observer.observe(el);
    });

    return () => {
      observer.disconnect();
      setActiveGroup(null);
    };
  }, [isMobileScreen]);

  return (
    <header
      className={clsx(
        "sticky top-[60px] z-[1] mb-8 flex flex-col gap-4 transition-all lg:top-0",
        isStuck &&
          "-ml-6 -mr-6 bg-white px-6 py-4 shadow-[2px_2px_10px_0_rgba(0,0,0,0.08)]"
      )}
    >
      <div
        className={clsx(
          "flex flex-row items-center gap-4 rounded-lg border bg-white transition-all",
          isStuck
            ? "border-transparent px-0 py-0"
            : "border-dm-charcoal-100 p-4 lg:px-8 lg:py-6"
        )}
      >
        <h1
          className={clsx(
            "font-serif font-bold !leading-10 text-dm-charcoal-800",
            isStuck ? "lg:text-lg" : "lg:text-2xl"
          )}
        >
          {titleOverride || "All Courses"}
        </h1>
        <div className="flex-1">
          {!disableSearch && <CourseSearch type="courses" />}
        </div>
      </div>

      <div className="flex gap-4">
        {groups.map((group) => (
          <Button
            key={group._id}
            className={clsx(
              "flex h-8 items-center !px-4 !py-0 lg:!px-8",
              activeGroup === group._id
                ? "bg-dm-charcoal-500 text-white hover:text-white"
                : "bg-white"
            )}
            type="outline"
            onClick={() => {
              window.scrollTo({
                top:
                  (document.getElementById(
                    group.name.replace(/\s+/g, "-").toLowerCase()
                  )?.offsetTop || 0) + 40,
                behavior: "smooth",
              });
            }}
          >
            {group.name}
          </Button>
        ))}
      </div>
    </header>
  );
};
